/**
 * First-leak-report onboarding nudges. As a new (free) user drills toward
 * their first leak report (25 signals), this pops a short, branded
 * encouragement modal at each milestone (1 / 6 / 13 / 19 signals) — the
 * cascade that carries them to the "AHA moment".
 *
 * Same auto-fire-off-a-query shape as the other modal providers, but the
 * once-per-milestone dedup is a localStorage high-water mark (per-device,
 * no backend) since progress already rides on `["billing-status"]`. The
 * 25-signal payoff is the existing `FirstLeakReportModal`; these stop before
 * it. Mounted in App.tsx after FirstLeakReport, before AchievementUnlock.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { getBillingStatus } from "@/features/billing/lib/billing";
import {
  lastCelebratedMilestone,
  markMilestoneCelebrated,
  pendingMilestone,
  type NudgeMilestone,
} from "@/features/onboarding/lib/firstReportNudge";
import { useAuth } from "@/features/auth/lib/authContext";
import { ICON_STROKE, X } from "@/shared/components/icons";
import { LogoMark } from "@/shared/components/LogoMark";

export function FirstReportNudgeProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus.kind === "signedIn";

  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
    enabled: signedIn,
  });

  const [milestone, setMilestone] = useState<NudgeMilestone | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!signedIn || open || !billing) return;
    // Only during the pre-first-report onboarding window of a FREE user, and
    // only after the welcome modal has been dismissed (don't stack on it).
    if (billing.subscriptionStatus !== "free") return;
    if (billing.firstLeakReportMilestoneShown) return;
    if (!billing.onboardingWelcomeShown) return;
    if (billing.signalCount >= billing.signalsForFirstReport) return;

    const due = pendingMilestone(billing.signalCount, lastCelebratedMilestone());
    if (!due) return;
    setMilestone(due);
    setOpen(true);
  }, [signedIn, billing, open]);

  const dismiss = () => {
    if (milestone) markMilestoneCelebrated(milestone.atSignals);
    setOpen(false);
    setMilestone(null);
  };

  return (
    <>
      {children}
      {open && milestone && billing && (
        <NudgeView
          milestone={milestone}
          signalCount={billing.signalCount}
          total={billing.signalsForFirstReport}
          onDismiss={dismiss}
        />
      )}
    </>
  );
}

function NudgeView({
  milestone,
  signalCount,
  total,
  onDismiss,
}: {
  milestone: NudgeMilestone;
  signalCount: number;
  total: number;
  onDismiss: () => void;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((signalCount / total) * 100)) : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(124,147,241,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 pt-8 space-y-4">
          <div className="mx-auto w-12 h-12 flex items-center justify-center">
            <LogoMark size={44} animate />
          </div>
          <h2 className="text-xl font-semibold text-text tracking-tight text-center">
            {milestone.title}
          </h2>
          <p className="text-sm text-text-muted leading-relaxed text-center">
            {milestone.body}
          </p>

          <div className="space-y-1.5 pt-1">
            <div className="h-2.5 rounded-full bg-bg/60 overflow-hidden">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-text-muted tabular-nums">
              <span>{signalCount} / {total}</span>
              <span>toward your first leak report</span>
            </div>
          </div>
        </div>

        <div className="px-6 pt-2 pb-5 border-t border-border/40">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
          >
            Keep drilling
          </button>
        </div>
      </div>
    </div>
  );
}
